import { Capacitor } from '@capacitor/core';
import { request } from '../api/client';

/**
 * Подписка браузера на уведомления (Web Push).
 *
 * Работает на компьютере и в Android-браузере; на iPhone — только если сайт
 * добавлен на экран «Домой». В Android-приложении не используется: там свой
 * канал через FCM.
 *
 * Важное ограничение, которое стоит знать: на компьютере уведомления приходят,
 * пока браузер запущен (свёрнутый годится), но не после его полного закрытия.
 * На Android браузер будит система, поэтому там они приходят и при закрытом
 * браузере.
 */
export async function registerWebPush(
  deviceId: string,
  onOpenChat: (chatId: string) => void,
): Promise<void> {
  // В нативной оболочке уведомления идут через FCM
  if (Capacitor.isNativePlatform()) {
    return;
  }
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return;
  }

  try {
    // Регистрируем обработчик заранее: он нужен и для показа уведомлений,
    // и для перехода в чат по нажатию
    const registration = await navigator.serviceWorker.register('/sw.js');

    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'open-chat' && typeof event.data.chatId === 'string') {
        onOpenChat(event.data.chatId);
      }
    });

    const config = await request<{ publicKey: string | null; enabled: boolean }>(
      '/push/public-key');
    if (!config.enabled || !config.publicKey) {
      return;
    }

    // Разрешение спрашиваем только если решение ещё не принято: повторный запрос
    // после отказа браузер всё равно проигнорирует, а «отказ» надо уважать
    if (Notification.permission === 'denied') {
      return;
    }
    if (Notification.permission === 'default') {
      const granted = await Notification.requestPermission();
      if (granted !== 'granted') {
        return;
      }
    }

    const existing = await registration.pushManager.getSubscription();
    const subscription = existing ?? await registration.pushManager.subscribe({
      // Без этого браузер не отдаст подписку: уведомления без содержимого
      // современными браузерами не поддерживаются
      userVisibleOnly: true,
      applicationServerKey: base64UrlToUint8Array(config.publicKey),
    });

    // Отправляем подписку целиком: в ней и адрес службы доставки,
    // и ключи, которыми сервер шифрует содержимое
    await request(`/auth/devices/${deviceId}/push-token`, {
      method: 'PUT',
      body: { token: JSON.stringify(subscription), provider: 'WEBPUSH' },
    });
  } catch (error) {
    // Отсутствие уведомлений не должно мешать переписке
    console.warn('Не удалось подключить уведомления в браузере', error);
  }
}

/**
 * Браузер ждёт ключ байтами, а сервер отдаёт его в base64url.
 * Массив создаётся поверх обычного ArrayBuffer намеренно: тип, выведенный
 * из `new Uint8Array(length)`, допускает и разделяемую память, которую
 * PushManager не принимает.
 */
function base64UrlToUint8Array(base64Url: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) {
    bytes[i] = raw.charCodeAt(i);
  }
  return bytes;
}
